import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Dictionary } from "lodash";

interface IContextProps {
  userIdsTypingByMessageThreadId: Dictionary<string[] | undefined>;
  updateStartedTyping: (messageThreadId: string, userId: string) => void;
  updateStoppedTyping: (messageThreadId: string, userId: string) => void;
}

const TypingContext = createContext<IContextProps>({
  userIdsTypingByMessageThreadId: {},
  updateStartedTyping: () => {},
  updateStoppedTyping: () => {},
});

export interface IProviderProps {
  children?: ReactNode;
}

export const TypingProvider = ({ children }: IProviderProps) => {
  const [userIdsTypingByMessageThreadId, setUserIdsTypingByMessageThreadId] =
    useState<Dictionary<string[] | undefined>>({});

  const updateStartedTyping = useCallback(
    (messageThreadId: string, userId: string) =>
      setUserIdsTypingByMessageThreadId((previous) => {
        if (previous[messageThreadId]?.includes(userId)) {
          return previous;
        }

        return {
          ...previous,
          [messageThreadId]: [...(previous[messageThreadId] ?? []), userId],
        };
      }),
    [],
  );

  const updateStoppedTyping = useCallback(
    (messageThreadId: string, userId: string) =>
      setUserIdsTypingByMessageThreadId((previous) => {
        if (!previous[messageThreadId]?.includes(userId)) {
          return previous;
        }

        const updatedUserIds = previous[messageThreadId]?.filter(
          (uid) => uid !== userId,
        );

        if (!updatedUserIds?.length) {
          const { [messageThreadId]: removedValue, ...rest } = previous;
          return rest;
        }

        return {
          ...previous,
          [messageThreadId]: updatedUserIds,
        };
      }),
    [],
  );

  const value = useMemo<IContextProps>(
    () => ({
      userIdsTypingByMessageThreadId,
      updateStartedTyping,
      updateStoppedTyping,
    }),
    [userIdsTypingByMessageThreadId, updateStartedTyping, updateStoppedTyping],
  );

  return (
    <TypingContext.Provider value={value}>{children}</TypingContext.Provider>
  );
};

export const useTyping = () => useContext(TypingContext);
